import * as React from "react";
import { RouteComponentProps } from "react-router";
import { Redirect } from "react-router-dom";

import { ProviderSet, ProviderSetService } from "@app/provider-sets";
import CreateProject from "./createProject";
import { QESCheckbox, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import WizardStep from "./WizardStep";
import { FeatureFlagContextProps, withFeatureFlagContext } from "../../feature-flags";
import Features from "../../feature-flags/features";
import { JSX } from "react";

interface CreateProjectStepProvidersProps extends RouteComponentProps, FeatureFlagContextProps {
  baseUrl: string;
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  hideUseInMarketAnalysis: boolean;
  isZipProject: boolean;
  onProvidersChange: (ps: ProviderSet) => void;
  onProvidersAliasChange: (alias: string) => void;
  onProspectsChange: (ps: ProviderSet) => void;
  onProspectAliasChange: (alias: string) => void;
  onUseMarketChange: (state: boolean) => void;
  onUseMarketAnalysisChange: (state: boolean) => void;
  onUseProspectAnalysisChange: (state: boolean) => void;
  onUseNetworkAnalysisChange: (state: boolean) => void;
  onMarketChange: (ps: ProviderSet) => void;
  onMarketAliasChange: (alias: string) => void;
  networkAlias: string;
  prospectAlias: string;
  marketAlias: string;

  progressList: string[];
  currentIndex: number;
}

interface CreateProjectStepProvidersState {
  loading: boolean;

  providerSets: ProviderSet[];
  marketProviderSets: ProviderSet[];

  prospects: ProviderSet | null;
  providers: ProviderSet | null;
  market: ProviderSet | null;

  useMarketProvidersForImpact: boolean;
  useMarketProvidersForAnalysis: boolean;
  useNetworkProvidersForAnalysis: boolean;
  useProspectProvidersForAnalysis: boolean;

  restartApp: boolean;
}

 class CreateProjectStepProviders extends React.Component<CreateProjectStepProvidersProps, CreateProjectStepProvidersState> {
  private readonly providerSetService = new ProviderSetService();

  constructor(props: CreateProjectStepProvidersProps) {
    super(props);

    /* eslint-disable sort-keys */
    this.state = {
      loading: true,

      marketProviderSets: [],
      providerSets: [],

      market: null,
      prospects: null,
      providers: null,
      useMarketProvidersForImpact: this.props.createProjectModel.useMarketProvidersForImpact,
      useMarketProvidersForAnalysis: this.props.createProjectModel.useMarketProvidersForMarketAnalysis,
      useNetworkProvidersForAnalysis: this.props.createProjectModel.useNetworkProvidersForMarketAnalysis,
      useProspectProvidersForAnalysis: this.props.createProjectModel.useProspectProvidersForMarketAnalysis,

      restartApp: !props.completionTracker.filter((_, index) => (props.currentIndex - 1) > index).every(step => step),
    };
    /* eslint-enable sort-keys */
  }

  public async componentDidMount() {
    const marketProviderSets = await this.providerSetService.getMarketProviderSets();
    const providerSets = await this.providerSetService.getProviderSets();

    this.setState({ loading: false, marketProviderSets, providerSets });
  }

  /** Renders the wizard step that selects providers and prospects. */
  public render() {
    if (this.state.restartApp) {
      return <Redirect to={`${this.props.baseUrl}/1`} />;
    }

    return (
      <WizardStep {...this.props} heading="Create a New Project" subheading="Define Analysis :: Providers" baseUrl={this.props.baseUrl} prevUrl={(this.props.currentIndex - 1).toString()} nextUrl={(this.props.currentIndex + 1).toString()} progressList={this.props.progressList} isNextDisabled={this.handleIsNextDisabled()}>
        <div className="">
          <div className="row">
            <div className="col-11 d-flex flex-wrap offset-1">
              <div className="col-4 label-s">Provider Set</div>
              {!this.props.isZipProject && <div className="col-3 label-s">Alias</div>}
            </div>
          </div>
          {this.renderNetworkType("Network", new Map([["", this.state.providerSets]]), this.state.providers, this.handleProvidersChanged, this.handleUseNetworkProvidersForAnalysisChange, this.state.useNetworkProvidersForAnalysis, this.props.createProjectModel.networkProviderSetId, this.props.hideUseInMarketAnalysis, this.props.networkAlias, this.props.onProvidersAliasChange, true)}
          {(!this.props.isZipProject || this.props.enabledFeatureFlags?.has(Features.ZIP_PROSPECT_AND_MARKET)) && (
            <>
              {this.renderNetworkType("Prospect", new Map([["", this.state.providerSets]]), this.state.prospects, this.handleProspectsChanged, this.handleUseProspectProvidersForAnalysisChange, this.state.useProspectProvidersForAnalysis, this.props.createProjectModel.prospectProviderSetId, this.props.hideUseInMarketAnalysis, this.props.prospectAlias, this.props.onProspectAliasChange)}
              {this.renderNetworkType("Market", new Map([["System Networks", this.state.marketProviderSets], ["Client Networks", this.state.providerSets]]), this.state.market, this.handleMarketChanged, this.handleUseMarketProvidersForAnalysisChange, this.state.useMarketProvidersForAnalysis, this.props.createProjectModel.marketProviderSetId, true, this.props.marketAlias, this.props.onMarketAliasChange)}
            </>
          )}
        </div>
      </WizardStep>
    );
  }

  /** Renders a select list, alias field, and checkbox for a particular type of provider set. */
  private renderNetworkType = (
    label: "Network" | "Prospect" | "Market",
    providerSets: Map<string, ProviderSet[]>,
    selectedProviderSet: ProviderSet | null,
    handler: React.ChangeEventHandler<HTMLSelectElement>,
    checkboxHandler: React.ChangeEventHandler<HTMLInputElement>,
    selectedCheckBoxValue: boolean,
    selectedValue: number,
    hideUseInMarketAnalysis: boolean,
    alias: string,
    aliasHandler: (alias: string) => void,
    autofocus?: boolean,
  ) => {
    const commonSelectProps: React.SelectHTMLAttributes<HTMLSelectElement> = {
      onChange: handler,
      value: selectedProviderSet ? selectedProviderSet.resource.resourceId : "",
    };

    const optGroups: JSX.Element[] = [];
    providerSets.forEach((providerSet, setLabel) => {
      if (setLabel === "") {
        providerSet.sort((a, b) => a.hasActivatedVersions > b.hasActivatedVersions ? -1 : 1).map((providerSet) => (
          // for some reason, the statuses return as "active" even though their type is an enum
          optGroups.push(providerSet.hasActivatedVersions ?
            <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId}>{providerSet.resource.name}</option> :
            <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId} disabled={true}>{providerSet.resource.name} - No Activated Versions</option>)))
      } else {
        optGroups.push(<optgroup label={setLabel} key={setLabel}> {
          providerSet.sort((a, b) => a.hasActivatedVersions > b.hasActivatedVersions ? -1 : 1).map((providerSet) => (
            providerSet.hasActivatedVersions ?
              <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId}>{providerSet.resource.name}</option> :
              <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId} disabled={true}>{providerSet.resource.name} - No Activated Versions</option>))
        }
        </optgroup>)
      }
    });

    const select = (
      <QESSelect
        label="Provider Set"
        {...commonSelectProps}
        value={selectedValue || ""}
        testId={"create-project-step-providers__" + label + "-select"}
        disabled={this.state.loading}
        autoFocus={autofocus}
        allowEmpty={true}
        hideLabel={true}
      >
        {
          optGroups
        }
      </QESSelect>
    );

    return (
      <>
        <div className="form-group row">
          <div className="col-1 align-items-center d-flex label-s">{label}</div>
          <div className="col-11 d-flex">
            <div className="col-4">{select}</div>
            {(!this.props.isZipProject || this.props.enabledFeatureFlags?.has(Features.ZIP_PROSPECT_AND_MARKET)) && (
              <QESTextInput
                containerClassName="col-3"
                label={"Alias"}
                value={alias || ""}
                onChange={(e) => { aliasHandler(e.target.value) }}
                testId={`create-project-step-providers__${label}-alias`}
                disabled={this.state.loading || !selectedValue}
                hideLabel={true}
              />
            )}
          </div>
          {!hideUseInMarketAnalysis &&
            <div className="col-4 offset-1 pl-4">
              <QESCheckbox
                label={"Use " + label + " Providers for Market Analysis"}
                checked={selectedCheckBoxValue}
                disabled={!selectedValue}
                onChange={checkboxHandler}
                testId={"create-project-step-providers__use-" + label + "-providers-for-analysis-checkbox"}
              />
            </div>
          }
        </div>
      </>
    );
  }

  private handleIsNextDisabled = () => {
    return !(
      this.props.createProjectModel.networkProviderSetId !== null &&
      this.props.createProjectModel.networkProviderSetId !== 0
    );
  }

  /** Handles when the provider set for providers is changed. */
  private handleProvidersChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) {
      const id = Number(value);
      const providerSet = this.state.providerSets.find((ps) => ps.resource.resourceId === id);
      this.setState({ providers: providerSet }, () => this.props.onProvidersChange(providerSet));
    } else {
      this.setState({ providers: null, useNetworkProvidersForAnalysis: false }, () => this.props.onProvidersChange(null));
      this.props.onUseNetworkAnalysisChange(false);
      this.props.onProvidersAliasChange("");
    }
  }

  /** Handles when the provider set for prospects is changed. */
  private handleProspectsChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) {
      const id = Number(value);
      const providerSet = this.state.providerSets.find((ps) => ps.resource.resourceId === id);
      this.setState({ prospects: providerSet }, () => this.props.onProspectsChange(providerSet));
    } else {
      this.setState({ prospects: null, useProspectProvidersForAnalysis: false }, () => this.props.onProspectsChange(null));
      this.props.onUseProspectAnalysisChange(false);
      this.props.onProspectAliasChange("");
    }
  }

  /** Handles when the "Use Market Providers" checkbox is checked or unchecked. */
  private handleUseMarketProvidersChange = (checked: boolean) => {
    this.setState({ useMarketProvidersForAnalysis: checked, useMarketProvidersForImpact: checked }, () => this.props.onUseMarketChange(checked));
  }

  private handleUseMarketProvidersForAnalysisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ useMarketProvidersForAnalysis: e.target.checked, useMarketProvidersForImpact: e.target.checked }, () => this.props.onUseMarketAnalysisChange(e.target.checked));
  }

  private handleUseNetworkProvidersForAnalysisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ useNetworkProvidersForAnalysis: e.target.checked }, () => this.props.onUseNetworkAnalysisChange(e.target.checked));
  }

  private handleUseProspectProvidersForAnalysisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ useProspectProvidersForAnalysis: e.target.checked }, () => this.props.onUseProspectAnalysisChange(e.target.checked));
  }

  /** Handles when the provider set for prospects is changed. */
  private handleMarketChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) {
      const id = Number(value);
      let providerSet = this.state.marketProviderSets.find((ps) => ps.resource.resourceId === id);
      if (!providerSet)
        providerSet = this.state.providerSets.find((ps) => ps.resource.resourceId === id);
      this.setState({ market: providerSet }, () => {
        this.props.onMarketChange(providerSet);
        this.handleUseMarketProvidersChange(true);
      });
    } else {
      this.setState({ market: null }, () => {
        this.props.onMarketChange(null);
        this.handleUseMarketProvidersChange(false);
      });
      this.props.onMarketAliasChange("");
    }
  }
}
export default withFeatureFlagContext(CreateProjectStepProviders);